import { Fragment, useState } from 'react';
import clsx from 'clsx';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import type { AgentTurnSchema } from '@avisoma/shared';
import CopyButton from '../../components/CopyButton';
import { ID } from '../../components/ID';
import { Link } from '../../components/Link';
import { formatDateTime, formatRelativeTime } from '../../lib/dateFormat';
import CoverageBadge from './CoverageBadge';
import TurnDetails from './TurnDetails';

type AgentTurn = AgentTurnSchema<string, string>;

// Same header and cell look as SortableTable. That table has no expandable rows, and the
// rows here arrive sorted (newest first) and paginated by the server, so a plain table fits.
const headClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-zinc-200 uppercase tracking-wider';
const cellClass = 'px-6 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-zinc-100';
const HEADERS = ['', 'Created', 'Coverage', 'Question', 'Gap', 'Project'];

export default function TurnsTable({ turns }: { turns: AgentTurn[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
        <thead className="bg-gray-50 dark:bg-zinc-900">
          <tr>
            {HEADERS.map((label, index) => (
              <th key={index} className={clsx(headClass, index === 0 && 'w-0 pr-0')}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-zinc-800 divide-y divide-gray-200 dark:divide-zinc-700">
          {turns.map((turn) => {
            const expanded = turn.id === expandedId;
            return (
              <Fragment key={turn.id}>
                <tr
                  className={clsx(
                    'cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-900',
                    expanded && 'bg-gray-50 dark:bg-zinc-900',
                  )}
                  onClick={() => setExpandedId(expanded ? null : turn.id)}
                  aria-expanded={expanded}
                >
                  <td className={clsx(cellClass, 'pr-0')}>
                    <ChevronRightIcon
                      className={clsx(
                        'size-4 text-gray-400 dark:text-zinc-500 transition-transform',
                        expanded && 'rotate-90',
                      )}
                    />
                  </td>
                  <td className={cellClass}>
                    <span title={formatRelativeTime(turn.createdAt)}>{formatDateTime(turn.createdAt)}</span>
                  </td>
                  <td className={cellClass}>
                    <CoverageBadge coverage={turn.coverage} />
                  </td>
                  <td className={cellClass}>
                    <span className="min-w-48 whitespace-normal line-clamp-2" title={turn.question}>
                      {turn.question}
                    </span>
                  </td>
                  <td className={cellClass}>
                    <span
                      className="min-w-48 whitespace-normal line-clamp-2 text-gray-500 dark:text-zinc-400"
                      title={turn.gap ?? undefined}
                    >
                      {turn.gap ?? '—'}
                    </span>
                  </td>
                  <td className={cellClass} onClick={(e) => e.stopPropagation()}>
                    <span className="flex items-center gap-2">
                      <Link
                        to={`/projects/${turn.projectId}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <ID id={turn.projectId} />
                      </Link>
                      <CopyButton text={turn.projectId} />
                    </span>
                  </td>
                </tr>
                {expanded && (
                  <tr className="bg-gray-50 dark:bg-zinc-900">
                    <td />
                    <td colSpan={HEADERS.length - 1} className="px-6 pb-4 text-sm">
                      <TurnDetails turn={turn} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
